import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './../components/sidebar';
import { Container, Row, Col, Card, Table, Spinner, Badge } from 'react-bootstrap';
import { FaUsers, FaCoins, FaEnvelope, FaChartBar, FaShoppingCart, FaTicketAlt, FaCheckCircle, FaTimesCircle, FaArrowUp } from 'react-icons/fa';
import Button from "react-bootstrap/Button";
import * as XLSX from 'xlsx';

const Reportesuser = () => {
  const [dataReport, setDataReport] = useState({
    loading: true,
    error: null,
    data: {
      totals: {
        cuponesEnviados: 0,
        cuponesAbiertosNoPagados: 0,
        cuponesPagados: 0,
        ganancias25: 0,
        ganancias25Compras: 0,
        ganancias25Upgrades: 0,
        totalVentas: 0,
        totalCompras: 0,
        totalUpgrades: 0
      },
      cupones: []
    }
  });

  const [users, setUsers] = useState({});
  const [wooOrders, setWooOrders] = useState([]);
  const [allRecomendaciones, setAllRecomendaciones] = useState([]); // Todas las recomendaciones para construir upline

  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem('loginUser');
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUsers(user);
    }
  }, []);

  // Obtener todos los pedidos de WooCommerce (todos los estados para detectar abiertos)
  const fetchWooCommerceOrders = async () => {
    try {
      // Obtener pedidos completados (pagados)
      const completedResponse = await axios.get('https://mrcirp.com/wp-json/wc/v3/pedidos-publicos/?status=completed');
      const completedOrders = completedResponse.data || [];
      
      // Obtener pedidos pendientes/processing (abiertos pero no pagados)
      try {
        const pendingResponse = await axios.get('https://mrcirp.com/wp-json/wc/v3/pedidos-publicos/?status=pending');
        const processingResponse = await axios.get('https://mrcirp.com/wp-json/wc/v3/pedidos-publicos/?status=processing');
        const pendingOrders = pendingResponse.data || [];
        const processingOrders = processingResponse.data || [];
        
        return [...completedOrders, ...pendingOrders, ...processingOrders];
      } catch (error) {
        // Si no se pueden obtener pedidos pendientes, solo retornar completados
        return completedOrders;
      }
    } catch (error) {
      console.error('Error fetching WooCommerce orders:', error);
      return [];
    }
  };

  // Obtener todas las recomendaciones para construir el upline
  const fetchAllRecomendaciones = async () => {
    try {
      // Obtener todas las recomendaciones (sin filtro de usuario)
      const response = await axios.get('https://api.mrcirp.com/public/reportes');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all recomendaciones:', error);
      return [];
    }
  };

  // Construir árbol de upline basado en la tabla recomendaciones
  // user_id = quien ENVIÓ la recomendación (sponsor)
  // amigo_email = correo del invitado (receptor del cupón)
  const buildUplineTree = (allRecomendacionesData, currentUserId) => {
    const tree = {};
    
    // Mapa: email_invitado -> user_id_sponsor (quien lo invitó)
    const emailToSponsorMap = {};
    
    // Mapa: user_id -> lista de emails que invitó
    const userIdToInvitedEmails = {};
    
    // Construir mapas de relaciones
    allRecomendacionesData.forEach(item => {
      const emailInvitado = item.amigo_email || item.correo;
      const sponsorId = item.user_id;
      
      if (emailInvitado && sponsorId) {
        emailToSponsorMap[emailInvitado.toLowerCase()] = sponsorId;
        
        if (!userIdToInvitedEmails[sponsorId]) {
          userIdToInvitedEmails[sponsorId] = [];
        }
        userIdToInvitedEmails[sponsorId].push(emailInvitado.toLowerCase());
        
        // Inicializar entrada en el árbol
        if (!tree[emailInvitado.toLowerCase()]) {
          tree[emailInvitado.toLowerCase()] = {
            email: emailInvitado,
            sponsorId: sponsorId,
            nivel: 1,
            esReferidoDirecto: sponsorId === currentUserId
          };
        }
      }
    });
    
    // Función recursiva para calcular el nivel en el upline
    const calcularNivel = (email, visited = new Set()) => {
      const emailLower = email.toLowerCase();
      
      // Evitar ciclos infinitos
      if (visited.has(emailLower)) {
        return 1;
      }
      visited.add(emailLower);
      
      const sponsorId = emailToSponsorMap[emailLower];
      if (!sponsorId) {
        return 1; // Si no tiene sponsor, es nivel 1
      }
      
      // Si el sponsor es el usuario actual, este es un referido directo (nivel 1)
      if (sponsorId === currentUserId) {
        return 1;
      }
      
      // Buscar si el sponsor también fue invitado por alguien
      // Necesitamos encontrar el email del sponsor en las recomendaciones
      const recomendacionSponsor = allRecomendacionesData.find(r => r.user_id === sponsorId);
      if (recomendacionSponsor) {
        const emailSponsor = recomendacionSponsor.amigo_email || recomendacionSponsor.correo;
        if (emailSponsor && emailToSponsorMap[emailSponsor.toLowerCase()]) {
          // El sponsor también fue invitado, entonces este es nivel 2+
          return calcularNivel(emailSponsor, visited) + 1;
        }
      }
      
      return 1; // Por defecto, nivel 1
    };
    
    // Calcular niveles para todos los emails en el árbol
    Object.keys(tree).forEach(email => {
      const nodo = tree[email];
      nodo.nivel = calcularNivel(email);
      nodo.esReferidoDirecto = nodo.sponsorId === currentUserId;
    });
    
    return tree;
  };

  // Obtener el email del sponsor basado en user_id
  const obtenerEmailSponsor = (sponsorId) => {
    if (!sponsorId || sponsorId === users.id) {
      return users.correo || users.email || 'Tú';
    }
    
    // Buscar en todas las recomendaciones
    const sponsorRecomendacion = allRecomendaciones.find(r => r.user_id === sponsorId);
    if (sponsorRecomendacion) {
      return sponsorRecomendacion.correo || sponsorRecomendacion.email || `ID: ${sponsorId}`;
    }
    
    return `ID: ${sponsorId}`;
  };

  // Determinar si una compra es COMPRA inicial o UPGRADE basado en orden cronológico
  // La primera compra es COMPRA, las siguientes son UPGRADE
  const clasificarCompras = (comprasPagadas) => {
    if (comprasPagadas.length === 0) {
      return { primeraCompra: null, upgrades: [] };
    }
    
    // Ordenar por fecha (más antigua primero)
    const comprasOrdenadas = [...comprasPagadas].sort((a, b) => {
      const fechaA = new Date(a.date_created || a.fecha || 0);
      const fechaB = new Date(b.date_created || b.fecha || 0);
      return fechaA - fechaB;
    });
    
    return {
      primeraCompra: comprasOrdenadas[0],
      upgrades: comprasOrdenadas.slice(1)
    };
  };

  const reportesGetUser = async () => {
    try {
      setDataReport(prev => ({...prev, loading: true, error: null}));
      
      // Obtener datos de referidos directos (cupones enviados por el usuario actual)
      // La API devuelve solo las recomendaciones donde user_id = users.id
      const response = await axios.get(`https://api.mrcirp.com/public/reportes/${users.id}`);
      const userData = response.data || [];
      
      // Obtener todas las recomendaciones para construir el upline
      const todasRecomendaciones = await fetchAllRecomendaciones();
      setAllRecomendaciones(todasRecomendaciones);
      
      // Construir árbol de upline
      const uplineTree = buildUplineTree(todasRecomendaciones, users.id);
      
      // Obtener todos los pedidos
      const orders = await fetchWooCommerceOrders();
      setWooOrders(orders);

      // Procesar cada cupón enviado (cada referido directo)
      const cuponesProcesados = userData.map((item, index) => {
        // En la tabla recomendaciones:
        // - correo = correo de quien envió (opcional, puede no estar)
        // - amigo_email = correo del amigo invitado (receptor del cupón)
        const emailReceptor = item.amigo_email || item.correo || '';
        
        // Buscar todas las compras de este referido usando el email del receptor
        const todasLasCompras = orders.filter(order => {
          const orderEmail = order.cliente_email || order.email || order.billing?.email || '';
          return orderEmail && orderEmail.toLowerCase() === emailReceptor.toLowerCase();
        });
        
        // Separar compras pagadas de abiertas/no pagadas
        const comprasPagadas = todasLasCompras.filter(order => 
          order.status === 'completed' || order.estado === 'completado'
        );
        
        const comprasAbiertas = todasLasCompras.filter(order => 
          order.status !== 'completed' && order.estado !== 'completado'
        );
        
        // Clasificar compras: primera es COMPRA, siguientes son UPGRADE
        const { primeraCompra, upgrades } = clasificarCompras(comprasPagadas);
        
        // Calcular totales
        const totalComprado = comprasPagadas.reduce((sum, order) => 
          sum + parseFloat(order.total || order.total_price || 0), 0
        );
        
        const totalCompras = primeraCompra 
          ? parseFloat(primeraCompra.total || primeraCompra.total_price || 0) 
          : 0;
        
        const totalUpgrades = upgrades.reduce((sum, order) => 
          sum + parseFloat(order.total || order.total_price || 0), 0
        );
        
        // Calcular ganancias del 25%
        const ganancia25Total = totalComprado * 0.25;
        const ganancia25Compras = totalCompras * 0.25;
        const ganancia25Upgrades = totalUpgrades * 0.25;
        
        // Estado del cupón
        const estaAbiertoNoPagado = comprasAbiertas.length > 0;
        const estaPagado = comprasPagadas.length > 0;
        
        // Obtener información del upline
        const referidoInfo = uplineTree[emailReceptor.toLowerCase()];
        const nivelUpline = referidoInfo ? referidoInfo.nivel : 1;
        const esReferidoDirecto = referidoInfo ? referidoInfo.esReferidoDirecto : true;
        
        // Obtener email del sponsor (quien envió la invitación)
        const sponsorId = item.user_id;
        const emailSponsor = obtenerEmailSponsor(sponsorId);
        
        // Verificar si este referido también tiene referidos (upline descendente)
        const tieneReferidos = todasRecomendaciones.some(r => {
          const emailRecomendador = r.correo || r.email || '';
          return emailRecomendador.toLowerCase() === emailReceptor.toLowerCase();
        });
        
        return {
          id: item.id || index + 1,
          email: emailReceptor, // Usar email como identificador (no hay código)
          nombre: item.amigo_nombre || item.nombre || 'Sin nombre',
          fechaEnvio: item.fecha || 'No especificada',
          programa: item.producto || 'Sin programa',
          
          // Estado del cupón
          estado: estaPagado ? 'pagado' : (estaAbiertoNoPagado ? 'abierto_no_pagado' : 'enviado'),
          
          // Datos de compras
          comprasPagadas: comprasPagadas,
          comprasAbiertas: comprasAbiertas,
          primeraCompra: primeraCompra,
          upgrades: upgrades,
          
          // Totales
          totalComprado: totalComprado,
          totalCompras: totalCompras,
          totalUpgrades: totalUpgrades,
          
          // Ganancias
          ganancia25Total: ganancia25Total,
          ganancia25Compras: ganancia25Compras,
          ganancia25Upgrades: ganancia25Upgrades,
          
          // Upline
          emailSponsor: emailSponsor,
          nivelUpline: nivelUpline,
          esReferidoDirecto: esReferidoDirecto,
          tieneReferidos: tieneReferidos,
          
          // Datos adicionales de la tabla recomendaciones
          nombreRecomendador: item.nombre || 'Sin nombre',
          correoRecomendador: item.correo || 'No especificado'
        };
      });

      // Calcular totales
      const cuponesEnviados = cuponesProcesados.length;
      const cuponesAbiertosNoPagados = cuponesProcesados.filter(c => c.estado === 'abierto_no_pagado').length;
      const cuponesPagados = cuponesProcesados.filter(c => c.estado === 'pagado').length;
      
      const totalVentas = cuponesProcesados.reduce((sum, c) => sum + c.totalComprado, 0);
      const totalCompras = cuponesProcesados.reduce((sum, c) => sum + c.totalCompras, 0);
      const totalUpgrades = cuponesProcesados.reduce((sum, c) => sum + c.totalUpgrades, 0);
      
      const ganancias25 = cuponesProcesados.reduce((sum, c) => sum + c.ganancia25Total, 0);
      const ganancias25Compras = cuponesProcesados.reduce((sum, c) => sum + c.ganancia25Compras, 0);
      const ganancias25Upgrades = cuponesProcesados.reduce((sum, c) => sum + c.ganancia25Upgrades, 0);

      setDataReport({
        loading: false,
        error: null,
        data: {
          totals: {
            cuponesEnviados,
            cuponesAbiertosNoPagados,
            cuponesPagados,
            ganancias25,
            ganancias25Compras,
            ganancias25Upgrades,
            totalVentas,
            totalCompras,
            totalUpgrades
          },
          cupones: cuponesProcesados
        }
      });

    } catch (error) {
      console.error('Error fetching user reports:', error);
      setDataReport({
        loading: false,
        error: 'Error al cargar los reportes',
        data: {
          totals: {
            cuponesEnviados: 0,
            cuponesAbiertosNoPagados: 0,
            cuponesPagados: 0,
            ganancias25: 0,
            ganancias25Compras: 0,
            ganancias25Upgrades: 0,
            totalVentas: 0,
            totalCompras: 0,
            totalUpgrades: 0
          },
          cupones: []
        }
      });
    }
  };

  useEffect(() => {
    if (users.id) {
      reportesGetUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.id]);

  const exportToExcel = () => {
    const excelData = dataReport.data.cupones.map((cupon, index) => ({
      '#': index + 1,
      'Email Receptor': cupon.email,
      'Nombre Receptor': cupon.nombre,
      'Fecha Envío': cupon.fechaEnvio,
      'Programa': cupon.programa,
      'Estado Cupón': cupon.estado === 'pagado' ? 'Pagado' : 
                      (cupon.estado === 'abierto_no_pagado' ? 'Abierto - No Pagado' : 'Enviado'),
      'Email Sponsor': cupon.emailSponsor,
      'Nivel Upline': cupon.nivelUpline,
      'Es Referido Directo': cupon.esReferidoDirecto ? 'Sí' : 'No',
      'Total Comprado': `$${cupon.totalComprado.toFixed(2)}`,
      'Total Compras': `$${cupon.totalCompras.toFixed(2)}`,
      'Total Upgrades': `$${cupon.totalUpgrades.toFixed(2)}`,
      'N° Compras': cupon.comprasPagadas.length,
      'N° Upgrades': cupon.upgrades.length,
      'Ganancia 25% Total': `$${cupon.ganancia25Total.toFixed(2)}`,
      'Ganancia 25% Compras': `$${cupon.ganancia25Compras.toFixed(2)}`,
      'Ganancia 25% Upgrades': `$${cupon.ganancia25Upgrades.toFixed(2)}`
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte Cupones");
    XLSX.writeFile(wb, `Reporte_Cupones_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (dataReport.loading) {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando tus reportes...</p>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  if (dataReport.error) {
    return (
      <Sidebar>
        <Container className="mt-4 mb-5">
          <Row className="justify-content-center">
            <Col md="auto">
              <div className="alert alert-danger">
                {dataReport.error}
              </div>
              <Button variant="primary" onClick={() => reportesGetUser()}>
                Reintentar
              </Button>
            </Col>
          </Row>
        </Container>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Container className="mt-4 mb-5">
        <Row className="mb-4">
          <Col>
            <h3><FaChartBar className="me-2" />Mis Reportes de Referidos</h3>
            <p className="text-muted">Resumen completo de cupones enviados, pagos y ganancias del 25%</p>
          </Col>
        </Row>

        {/* Tarjetas de Resumen */}
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Cupones Enviados</h6>
                    <h3>{dataReport.data.totals.cuponesEnviados}</h3>
                    <small className="text-muted">Total de referidos</small>
                  </div>
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FaEnvelope size={24} className="text-primary" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Abiertos - No Pagados</h6>
                    <h3>{dataReport.data.totals.cuponesAbiertosNoPagados}</h3>
                    <small className="text-muted">Pendientes de pago</small>
                  </div>
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FaTicketAlt size={24} className="text-warning" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">Cupones Pagados</h6>
                    <h3>{dataReport.data.totals.cuponesPagados}</h3>
                    <small className="text-muted">Compradores activos</small>
                  </div>
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FaCheckCircle size={24} className="text-success" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">25% Ganancias</h6>
                    <h3>${dataReport.data.totals.ganancias25.toFixed(2)}</h3>
                    <small className="text-muted">
                      ${dataReport.data.totals.ganancias25Compras.toFixed(2)} compras + 
                      ${dataReport.data.totals.ganancias25Upgrades.toFixed(2)} upgrades
                    </small>
                  </div>
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FaCoins size={24} className="text-info" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Cupones */}
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title className="mb-4">Cupones Enviados y Estado de Pagos</Card.Title>
                
                {dataReport.data.cupones.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Email Receptor</th>
                          <th>Receptor</th>
                          <th>Fecha Envío</th>
                          <th>Estado Cupón</th>
                          <th>Upline</th>
                          <th>Concepto</th>
                          <th>Monto</th>
                          <th>25% Ganancias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataReport.data.cupones.map((cupon, index) => (
                          <tr key={cupon.id}>
                            <td>{index + 1}</td>
                            <td>
                              <small className="text-muted">{cupon.email}</small>
                            </td>
                            <td>{cupon.nombre}</td>
                            <td>{cupon.fechaEnvio}</td>
                            <td>
                              {cupon.estado === 'pagado' && (
                                <Badge bg="success">
                                  <FaCheckCircle className="me-1" />
                                  Pagado
                                </Badge>
                              )}
                              {cupon.estado === 'abierto_no_pagado' && (
                                <Badge bg="warning">
                                  <FaTicketAlt className="me-1" />
                                  Abierto - No Pagado
                                </Badge>
                              )}
                              {cupon.estado === 'enviado' && (
                                <Badge bg="secondary">
                                  <FaEnvelope className="me-1" />
                                  Enviado
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div>
                                <small className="text-muted">Sponsor:</small>
                                <br />
                                <Badge bg="info">{cupon.emailSponsor}</Badge>
                                {cupon.nivelUpline > 0 && (
                                  <>
                                    <br />
                                    <small className="text-muted">
                                      Nivel {cupon.nivelUpline}
                                      {cupon.esReferidoDirecto && (
                                        <FaArrowUp className="ms-1" title="Referido directo" />
                                      )}
                                    </small>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              {cupon.comprasPagadas.length > 0 && (
                                <div>
                                  {cupon.primeraCompra && (
                                    <Badge bg="primary" className="me-1">COMPRA</Badge>
                                  )}
                                  {cupon.upgrades.length > 0 && (
                                    <>
                                      {cupon.upgrades.map((upgrade, idx) => (
                                        <Badge key={idx} bg="success" className="me-1">
                                          UPGRADE {idx + 1}
                                        </Badge>
                                      ))}
                                    </>
                                  )}
                                </div>
                              )}
                              {cupon.comprasPagadas.length === 0 && (
                                <span className="text-muted">Sin compras</span>
                              )}
                            </td>
                            <td>
                              {cupon.totalComprado > 0 ? (
                                <div>
                                  <div className="fw-bold text-success">
                                    ${cupon.totalComprado.toFixed(2)}
                                  </div>
                                  {cupon.totalCompras > 0 && (
                                    <small className="text-muted d-block">
                                      Compra: ${cupon.totalCompras.toFixed(2)}
                                    </small>
                                  )}
                                  {cupon.totalUpgrades > 0 && (
                                    <small className="text-muted d-block">
                                      Upgrades: ${cupon.totalUpgrades.toFixed(2)}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                '$0.00'
                              )}
                            </td>
                            <td>
                              {cupon.ganancia25Total > 0 ? (
                                <div>
                                  <div className="fw-bold text-primary">
                                    ${cupon.ganancia25Total.toFixed(2)}
                                  </div>
                                  {cupon.ganancia25Compras > 0 && (
                                    <small className="text-muted d-block">
                                      Compra: ${cupon.ganancia25Compras.toFixed(2)}
                                    </small>
                                  )}
                                  {cupon.ganancia25Upgrades > 0 && (
                                    <small className="text-muted d-block">
                                      Upgrades: ${cupon.ganancia25Upgrades.toFixed(2)}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                '$0.00'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No tienes cupones enviados aún.</p>
                    <Button variant="success" size="sm">
                      Invitar contactos
                    </Button>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-3">
                  <div>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={exportToExcel}
                      disabled={dataReport.data.cupones.length === 0}
                    >
                      Exportar a Excel
                    </Button>
                  </div>
                  <div>
                    <Button variant="success" size="sm" onClick={() => reportesGetUser()}>
                      Actualizar datos
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Sidebar>
  );
};

export default Reportesuser;
